import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddOutlined,
  DeleteOutlined,
  DescriptionOutlined,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { PORTAL_ROUTES } from '../../routes';
import { listFamilyMembers, uploadFileToPresignedUrl } from '../../services/familyApi';
import {
  createDocument,
  createDocumentUpload,
  deleteDocument,
  listDocuments,
} from '../../services/documentApi';
import DocumentDetailsDialog, { formatDocumentType } from './components/DocumentDetailsDialog';

function formatMemberName(member) {
  if (!member) {
    return '';
  }

  return [member.firstName, member.middleNames, member.lastName].filter(Boolean).join(' ');
}

function formatDate(value) {
  if (!value) {
    return 'Unknown';
  }

  const parsedValue = new Date(value);
  if (Number.isNaN(parsedValue.getTime())) {
    return value;
  }

  return parsedValue.toLocaleString();
}

function DocumentsWorkspace({ account }) {
  const fileInputRef = useRef(null);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [screenError, setScreenError] = useState('');
  const [uploadBusy, setUploadBusy] = useState(false);
  const [dialogDocument, setDialogDocument] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const selectedMember = useMemo(
    () => members.find((member) => member.memberId === selectedMemberId) ?? null,
    [members, selectedMemberId]
  );
  const dialogMember = useMemo(
    () => members.find((member) => member.memberId === dialogDocument?.memberId) ?? null,
    [dialogDocument?.memberId, members]
  );
  useEffect(() => {
    let ignore = false;

    async function loadMembers() {
      if (!account?.token) {
        setMembers([]);
        setMembersLoading(false);
        return;
      }

      setMembersLoading(true);
      setScreenError('');

      try {
        const response = await listFamilyMembers({ token: account.token });
        if (ignore) {
          return;
        }

        const nextMembers = response.members ?? [];
        setMembers(nextMembers);
        setSelectedMemberId((current) => {
          if (current && nextMembers.some((member) => member.memberId === current)) {
            return current;
          }

          return nextMembers[0]?.memberId ?? '';
        });
      } catch (error) {
        if (!ignore) {
          setScreenError(error.message);
        }
      } finally {
        if (!ignore) {
          setMembersLoading(false);
        }
      }
    }

    loadMembers();
    return () => {
      ignore = true;
    };
  }, [account?.token]);

  useEffect(() => {
    let ignore = false;

    async function loadDocuments() {
      if (!account?.token || !selectedMemberId) {
        setDocuments([]);
        return;
      }

      setDocumentsLoading(true);
      setScreenError('');

      try {
        const response = await listDocuments({ token: account.token, memberId: selectedMemberId });
        if (!ignore) {
          setDocuments(response.documents ?? []);
        }
      } catch (error) {
        if (!ignore) {
          setScreenError(error.message);
        }
      } finally {
        if (!ignore) {
          setDocumentsLoading(false);
        }
      }
    }

    loadDocuments();
    return () => {
      ignore = true;
    };
  }, [account?.token, selectedMemberId]);

  async function handleFilePicked(event) {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file || !account?.token || !selectedMemberId) {
      return;
    }

    setUploadBusy(true);
    setScreenError('');

    try {
      const upload = await createDocumentUpload({
        token: account.token,
        memberId: selectedMemberId,
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
      });

      await uploadFileToPresignedUrl({
        uploadUrl: upload.uploadUrl,
        file,
        contentType: file.type || 'application/octet-stream',
      });

      const createdDocument = await createDocument({
        token: account.token,
        memberId: selectedMemberId,
        documentId: upload.documentId,
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
      });

      setDocuments((current) => [createdDocument, ...current]);
    } catch (error) {
      setScreenError(error.message);
    } finally {
      setUploadBusy(false);
    }
  }

  function openDocumentDialog(document) {
    setDialogDocument(document);
  }

  function closeDialog() {
    setDialogDocument(null);
  }

  function handleDialogSave(updatedDocument) {
    setDocuments((current) =>
      current.map((document) => (document.documentId === updatedDocument.documentId ? updatedDocument : document))
    );
    setDialogDocument(updatedDocument);
  }

  async function handleDeleteDocument(document) {
    if (!account?.token || !document) {
      return;
    }

    setDeleteTarget(document);
    setDeleteError('');
  }

  async function handleConfirmDelete() {
    if (!account?.token || !deleteTarget) {
      return;
    }

    setDeleteBusy(true);
    setDeleteError('');

    try {
      await deleteDocument({
        token: account.token,
        memberId: deleteTarget.memberId,
        documentId: deleteTarget.documentId,
      });
      setDocuments((current) =>
        current.filter((item) => item.documentId !== deleteTarget.documentId)
      );

      if (dialogDocument?.documentId === deleteTarget.documentId) {
        closeDialog();
      }

      setDeleteTarget(null);
    } catch (error) {
      setDeleteError(error.message);
    } finally {
      setDeleteBusy(false);
    }
  }

  if (membersLoading) {
    return (
      <Box sx={{ py: 4 }}>
        <Stack alignItems="center" spacing={1.5}>
          <CircularProgress size={28} />
          <Typography color="text.secondary" variant="body2">
            Loading family members and documents...
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (members.length === 0) {
    return (
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'center',
          minHeight: { xs: 320, md: 420 },
          width: '100%',
        }}
      >
        <Paper
          sx={{
            maxWidth: 520,
            px: { xs: 2.5, sm: 4 },
            py: { xs: 3, sm: 4 },
            textAlign: 'center',
            width: '100%',
          }}
          variant="outlined"
        >
          <Stack alignItems="center" spacing={1.5}>
            <Typography sx={{ fontWeight: 700 }} variant="h6">
              Add a family member first
            </Typography>
            <Typography color="text.secondary" variant="body2">
              You need to have at least one family member before managing documents. Add your first one{' '}
              <Link component={RouterLink} to={PORTAL_ROUTES.family} underline="hover">
                here
              </Link>
              .
            </Typography>
          </Stack>
        </Paper>
      </Box>
    );
  }

  return (
    <>
      <input
        hidden
        onChange={handleFilePicked}
        ref={fileInputRef}
        type="file"
      />

      {screenError ? <Alert severity="error" sx={{ mb: 2.5 }}>{screenError}</Alert> : null}

      <Box>
        <TextField
          label="Family member"
          onChange={(event) => setSelectedMemberId(event.target.value)}
          select
          size="small"
          sx={{ display: 'flex', mb: 2, ml: 'auto', maxWidth: 360, width: '100%' }}
          value={selectedMemberId}
        >
          {members.map((member) => (
            <MenuItem key={member.memberId} value={member.memberId}>
              {formatMemberName(member)} ({member.relationship})
            </MenuItem>
          ))}
        </TextField>

        {documentsLoading ? (
          <Paper sx={{ p: 3 }} variant="outlined">
            <Stack alignItems="center" spacing={1.5}>
              <CircularProgress size={24} />
              <Typography color="text.secondary" variant="body2">
                Loading documents for {formatMemberName(selectedMember) || 'this family member'}...
              </Typography>
            </Stack>
          </Paper>
        ) : documents.length > 0 ? (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Document</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Uploaded</TableCell>
                  <TableCell align="right"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documents.map((document) => {
                  return (
                    <TableRow
                      hover
                      key={document.documentId}
                      onClick={() => openDocumentDialog(document)}
                      sx={{
                        cursor: 'pointer',
                        '&:last-child td, &:last-child th': { borderBottom: 0 },
                      }}
                    >
                      <TableCell sx={{ minWidth: 220, maxWidth: 0 }}>
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                          <Box
                            sx={{
                              alignItems: 'center',
                              bgcolor: 'background.default',
                              borderRadius: 1.5,
                              color: 'text.secondary',
                              display: 'flex',
                              height: 36,
                              justifyContent: 'center',
                              width: 36,
                            }}
                          >
                            <DescriptionOutlined fontSize="small" />
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography noWrap sx={{ display: 'block', fontWeight: 600 }}>
                              {document.fileName}
                            </Typography>
                            <Typography color="text.secondary" noWrap variant="body2">
                              Click row to open
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip label={formatDocumentType(document.documentType)} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{formatDate(document.createdAtUtc)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Remove document">
                          <span>
                            <IconButton
                              aria-label={`delete ${document.fileName}`}
                              color="default"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleDeleteDocument(document);
                              }}
                              size="small"
                            >
                              <DeleteOutlined fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Paper sx={{ p: 3 }} variant="outlined">
            <Typography variant="subtitle1">No documents yet</Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }} variant="body2">
              Upload files for {formatMemberName(selectedMember) || 'this family member'} and they will show up here.
            </Typography>
          </Paper>
        )}

        <Button
          aria-label={uploadBusy ? 'Uploading document' : 'Add document'}
          disabled={uploadBusy}
          onClick={() => fileInputRef.current?.click()}
          variant="outlined"
          sx={{
            borderRadius: 2,
            borderStyle: 'dashed',
            borderWidth: 2,
            color: 'text.secondary',
            minHeight: 64,
            mt: 2,
            width: '100%',
          }}
        >
          <AddOutlined />
        </Button>
      </Box>

      <DocumentDetailsDialog
        account={account}
        document={dialogDocument}
        members={members}
        onClose={closeDialog}
        onSaved={handleDialogSave}
        open={Boolean(dialogDocument)}
      />

      <Dialog
        onClose={() => {
          if (!deleteBusy) {
            setDeleteError('');
            setDeleteTarget(null);
          }
        }}
        open={Boolean(deleteTarget)}
      >
        <DialogTitle>Delete document</DialogTitle>
        <DialogContent sx={{ pt: '10px !important' }}>
          <Stack spacing={1.5}>
            {deleteError ? <Alert severity="error">{deleteError}</Alert> : null}
            <Typography variant="body2">
              Delete {deleteTarget?.fileName || 'this document'} for {formatMemberName(dialogMember && deleteTarget?.memberId === dialogMember.memberId ? dialogMember : selectedMember) || 'this family member'}?
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            disabled={deleteBusy}
            onClick={() => {
              setDeleteError('');
              setDeleteTarget(null);
            }}
            variant="text"
          >
            Cancel
          </Button>
          <Button color="error" disabled={deleteBusy} onClick={handleConfirmDelete} variant="contained">
            {deleteBusy ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default DocumentsWorkspace;
